import type { FC, ReactNode } from 'react'

import { ArrowRight } from 'lucide-react'
import { ButtonAnchor, ButtonLink } from '@/components/generals/button'
import { WhatsAppIcon } from '@/components/generals/icons'
import { whatsappLink } from '@/constants/site'

interface CtaBannerProps {
    title?: ReactNode
    description?: string
    primaryLabel?: string
}

export const CtaBanner: FC<CtaBannerProps> = ({
    title = (
        <>
            Let&apos;s create something
            {' '}
            <em className='text-gold-soft'>extraordinary</em>
        </>
    ),
    description = 'Contact us today and let us turn your vision into a luxury experience. Tell us what you are planning and we will call you back to talk it through.',
    primaryLabel = 'Plan your event'
}): ReactNode => {
    return (
        <section id='get-in-touch' aria-labelledby='cta-title' className='bg-ivory px-4 py-16 sm:px-6 sm:py-20'>
            <div className='surface-crimson relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] px-6 py-16 text-center text-white sm:px-12 lg:py-20'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:22px_22px] opacity-40' />

                <div className='relative mx-auto max-w-3xl'>
                    <h2 id='cta-title' className='font-heading text-4xl leading-[1.05] tracking-tight text-balance sm:text-5xl lg:text-6xl'>
                        {title}
                    </h2>

                    <p className='mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg'>
                        {description}
                    </p>

                    <div className='mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row'>
                        <ButtonLink href='/contact#inquiry' variant='light' size='lg'>
                            {primaryLabel}
                            <ArrowRight className='size-4' aria-hidden='true' />
                        </ButtonLink>

                        <ButtonAnchor
                            href={whatsappLink('Hi Guju Events, I would like to plan an event.')}
                            target='_blank'
                            rel='noopener noreferrer'
                            variant='outline-light'
                            size='lg'
                        >
                            <WhatsAppIcon className='size-4' />
                            WhatsApp us
                        </ButtonAnchor>
                    </div>
                </div>
            </div>
        </section>
    )
}
